import { useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { Gift, Loader2 } from 'lucide-react';
import type { BundleAdminListItemToken } from '@sleed/bizcommerce-api';
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { SystemGrid, type SystemGridColumn } from '~/components/system/grid/system-grid';
import { useBundlesList, type BundlesListFilter } from '~/hooks/use-bundles';
import { BUNDLE_TYPE, BUNDLES_PATH, GIFTBOX_TYPE } from '~/lib/bundles';
import { formatPercent } from '~/lib/dashboard/format';
import type { SortDir } from '~/lib/grid/sort';
import { ProductCombobox, ProductThumb, type ProductComboboxValue } from './product-combobox';
import { BundleStatus } from './bundle-status';
import { formatActiveWindow, money, useBundleCodeLabel } from './bundle-labels';

type Row = BundleAdminListItemToken;

/** Dialog listing Bundle/Giftbox products without contents; picking one opens its detail page. */
function NewBundleDialog({ open, onOpenChange }: { open: boolean; onOpenChange: (open: boolean) => void }) {
	const { t } = useTranslation();
	const navigate = useNavigate();
	const label = useBundleCodeLabel();
	const all = useBundlesList({ hasContents: false }, open);
	const empty = all.data?.items ?? [];

	return (
		<Dialog open={open} onOpenChange={onOpenChange}>
			<DialogContent className="sm:max-w-xl">
				<DialogHeader>
					<DialogTitle>{t('catalog.bundles.newTitle')}</DialogTitle>
					<DialogDescription>{t('catalog.bundles.newBody')}</DialogDescription>
				</DialogHeader>
				<div className="max-h-[50vh] overflow-y-auto rounded-md border border-border">
					{all.isLoading ? (
						<div className="flex items-center justify-center gap-2 py-8 text-sm text-foreground-muted">
							<Loader2 size={14} className="animate-spin" /> {t('common.loading')}
						</div>
					) : empty.length === 0 ? (
						<p className="p-6 text-center text-sm text-foreground-muted">{t('catalog.bundles.newEmpty')}</p>
					) : (
						empty.map(r => (
							<button
								key={r.bundle?.id}
								type="button"
								onClick={() => navigate(`${BUNDLES_PATH}/${r.bundle?.id}`)}
								className="flex w-full items-center gap-3 border-b border-border-subtle px-3 py-2 text-left last:border-b-0 hover:bg-elevated">
								<ProductThumb src={r.bundle?.img} alt={r.bundle?.nm} size={32} />
								<span className="min-w-0 flex-1">
									<span className="block truncate text-sm font-medium text-foreground">{r.bundle?.nm || `#${r.bundle?.id}`}</span>
									<span className="block font-mono text-[11px] text-foreground-muted">{r.bundle?.cd}</span>
								</span>
								<span className="text-xs text-foreground-muted">{label.type(r.bundle?.ptype)}</span>
							</button>
						))
					)}
				</div>
			</DialogContent>
		</Dialog>
	);
}

/** Bundles list — one row per Bundle/Giftbox product with totals and checkout status; the detail page edits contents. */
export function BundlesGridView() {
	const { t } = useTranslation();
	const navigate = useNavigate();
	const label = useBundleCodeLabel();
	const [type, setType] = useState<string>('all');
	const [status, setStatus] = useState<string>('all');
	const [contains, setContains] = useState<ProductComboboxValue | null>(null);
	const [newOpen, setNewOpen] = useState(false);
	const [pageIndex, setPageIndex] = useState(0);
	const [pageSize, setPageSize] = useState(25);
	// The API pages but doesn't search or sort (rows come in product-id order), so both apply to the loaded page only.
	const [search, setSearch] = useState('');
	const [sort, setSort] = useState<{ key: string; dir: SortDir } | null>(null);

	// A filter change goes back to the first page.
	const onFilter =
		<V,>(set: (value: V) => void) =>
		(value: V) => {
			set(value);
			setPageIndex(0);
		};

	const filter = useMemo<BundlesListFilter>(
		() => ({
			type: type === 'all' ? undefined : Number(type),
			isActive: status === 'all' ? undefined : status === 'active',
			containsProductId: contains?.id,
			skip: pageIndex * pageSize,
			take: pageSize
		}),
		[type, status, contains, pageIndex, pageSize]
	);
	const { data, isLoading, isError, refetch } = useBundlesList(filter);
	const total = data?.total ?? 0;

	// Step back when the current page empties out (e.g. the last row on it was removed).
	useEffect(() => {
		if (pageIndex > 0 && pageIndex * pageSize >= total && !isLoading && data) setPageIndex(Math.max(0, Math.ceil(total / pageSize) - 1));
	}, [pageIndex, pageSize, total, isLoading, data]);

	const columns = useMemo<SystemGridColumn<Row>[]>(
		() => [
			{
				key: 'image',
				header: t('catalog.bundles.columns.image'),
				width: '70px',
				toggleable: false,
				render: r => <ProductThumb src={r.bundle?.img} alt={r.bundle?.nm} size={36} />
			},
			{
				key: 'name',
				header: t('catalog.bundles.columns.name'),
				width: 'minmax(0,1.6fr)',
				sortValue: r => r.bundle?.nm ?? '',
				exportValue: r => r.bundle?.nm ?? '',
				render: r => (
					<span className="truncate text-sm font-medium text-foreground" title={r.bundle?.nm ?? ''}>
						{r.bundle?.nm || '—'}
					</span>
				)
			},
			{
				key: 'code',
				header: t('catalog.bundles.columns.code'),
				width: '130px',
				sortValue: r => r.bundle?.cd ?? '',
				exportValue: r => r.bundle?.cd ?? '',
				render: r => <span className="font-mono text-xs text-foreground-muted">{r.bundle?.cd || '—'}</span>
			},
			{
				key: 'type',
				header: t('catalog.bundles.columns.type'),
				width: '100px',
				sortValue: r => r.bundle?.ptype ?? 0,
				exportValue: r => label.type(r.bundle?.ptype),
				render: r => <span className="text-sm text-foreground-muted">{label.type(r.bundle?.ptype)}</span>
			},
			{
				key: 'count',
				header: t('catalog.bundles.columns.count'),
				width: '90px',
				align: 'right',
				sortValue: r => r.cnt ?? 0,
				exportValue: r => r.cnt ?? 0,
				render: r => <span className="text-sm tabular-nums text-foreground-muted">{r.cnt ?? 0}</span>
			},
			{
				key: 'regular',
				header: t('catalog.bundles.columns.regularTotal'),
				width: '120px',
				align: 'right',
				sortValue: r => r.rtot ?? 0,
				exportValue: r => r.rtot ?? 0,
				render: r => <span className="text-sm tabular-nums">{r.bundle?.ptype === BUNDLE_TYPE ? money(r.rtot) : '—'}</span>
			},
			{
				key: 'bundleTotal',
				header: t('catalog.bundles.columns.bundleTotal'),
				width: '120px',
				align: 'right',
				sortValue: r => r.btot ?? 0,
				exportValue: r => r.btot ?? 0,
				render: r => <span className="text-sm font-medium tabular-nums">{r.bundle?.ptype === BUNDLE_TYPE ? money(r.btot) : '—'}</span>
			},
			{
				key: 'saving',
				header: t('catalog.bundles.columns.saving'),
				width: '140px',
				align: 'right',
				sortValue: r => r.sav ?? 0,
				exportValue: r => r.sav ?? 0,
				render: r =>
					r.bundle?.ptype === BUNDLE_TYPE ? (
						<span className="text-sm tabular-nums text-success-fg">
							{money(r.sav)} <span className="text-xs text-foreground-muted">({formatPercent(r.savp ?? 0)})</span>
						</span>
					) : (
						<span className="text-foreground-disabled">—</span>
					)
			},
			{
				key: 'status',
				header: t('catalog.bundles.columns.status'),
				width: 'minmax(0,1.2fr)',
				sortValue: r => (r.act ? 1 : 0),
				exportValue: r => (r.act ? t('catalog.bundles.status.active') : (r.rsn ?? []).map(label.reason).join(', ')),
				render: r => <BundleStatus active={r.act} reasons={r.rsn} compact />
			},
			{
				key: 'dates',
				header: t('catalog.bundles.columns.dates'),
				width: '260px',
				exportValue: r => formatActiveWindow(r.afrom, r.ato),
				render: r => <span className="text-xs text-foreground-muted">{formatActiveWindow(r.afrom, r.ato)}</span>
			}
		],
		[t, label]
	);

	// Search and sort over the loaded page (see above); the pager still counts every server match.
	const pageRows = useMemo(() => {
		const q = search.trim().toLowerCase();
		const rows = q
			? (data?.items ?? []).filter(r => `${r.bundle?.nm ?? ''} ${r.bundle?.cd ?? ''}`.toLowerCase().includes(q))
			: (data?.items ?? []);
		const getVal = sort ? columns.find(c => c.key === sort.key)?.sortValue : undefined;
		if (!sort || !getVal) return rows;
		const dir = sort.dir === 'asc' ? 1 : -1;
		return [...rows].sort((a, b) => {
			const va = getVal(a) ?? '';
			const vb = getVal(b) ?? '';
			return (typeof va === 'number' && typeof vb === 'number' ? va - vb : String(va).localeCompare(String(vb))) * dir;
		});
	}, [data, search, sort, columns]);

	const toolbarExtras = (
		<>
			<Select value={type} onValueChange={onFilter(setType)}>
				<SelectTrigger className="h-9 w-36" aria-label={t('catalog.bundles.filters.type')}>
					<SelectValue />
				</SelectTrigger>
				<SelectContent>
					<SelectItem value="all">{t('catalog.bundles.filters.allTypes')}</SelectItem>
					<SelectItem value={String(BUNDLE_TYPE)}>{t('catalog.bundles.types.bundle')}</SelectItem>
					<SelectItem value={String(GIFTBOX_TYPE)}>{t('catalog.bundles.types.giftbox')}</SelectItem>
				</SelectContent>
			</Select>
			<Select value={status} onValueChange={onFilter(setStatus)}>
				<SelectTrigger className="h-9 w-36" aria-label={t('catalog.bundles.filters.status')}>
					<SelectValue />
				</SelectTrigger>
				<SelectContent>
					<SelectItem value="all">{t('catalog.bundles.filters.allStatuses')}</SelectItem>
					<SelectItem value="active">{t('catalog.bundles.status.active')}</SelectItem>
					<SelectItem value="inactive">{t('catalog.bundles.status.inactive')}</SelectItem>
				</SelectContent>
			</Select>
			<ProductCombobox
				className="w-72"
				value={contains}
				placeholder={t('catalog.bundles.filters.containsProduct')}
				onChange={p => p.id != null && onFilter(setContains)({ id: p.id, cd: p.cd, nm: p.nm, img: p.img1 })}
				onClear={() => onFilter(setContains)(null)}
			/>
		</>
	);

	return (
		<>
			<SystemGrid<Row>
				gridId="bundles"
				title={t('catalog.bundles.title')}
				subtitle={t('catalog.bundles.subtitle')}
				rows={pageRows}
				loading={isLoading}
				error={isError}
				onRetry={() => void refetch()}
				getRowId={r => r.bundle?.id ?? 0}
				columns={columns}
				server={{
					totalCount: total,
					pageIndex,
					pageSize,
					onPageChange: setPageIndex,
					onPageSizeChange: size => {
						setPageSize(size);
						setPageIndex(0);
					},
					sort,
					onSortChange: setSort,
					search,
					onSearchChange: setSearch
				}}
				toolbarExtras={toolbarExtras}
				emptyIcon={<Gift size={36} className="text-foreground-disabled" strokeWidth={1.25} />}
				addLabel={t('catalog.bundles.add')}
				onAdd={() => setNewOpen(true)}
				onEdit={r => navigate(`${BUNDLES_PATH}/${r.bundle?.id}`)}
				onRowClick={r => navigate(`${BUNDLES_PATH}/${r.bundle?.id}`)}
				exportFilename="bundles"
			/>
			<NewBundleDialog open={newOpen} onOpenChange={setNewOpen} />
		</>
	);
}
